import React, { useState } from 'react';
import { 
  Building2, 
  ShoppingBag, 
  Smartphone, 
  Palette, 
  TrendingUp, 
  Users, 
  ArrowRight,
  CheckCircle,
  Target,
  Globe,
  Printer,
  Droplet,
  Layers,
  Scale
} from 'lucide-react';

export default function ServicesTabSection() {
  const [activeTab, setActiveTab] = useState('industrial');

  const industrialServices = [
  {
    icon: Printer,
    title: "TIJ Printers",
    description: "High-resolution Thermal Inkjet printers designed for precise and clean printing on all packaging materials.",
    features: [
      "Sharp & Smudge-Free Printing",
      "Low Maintenance Design",
      "Quick Cartridge Change",
      "Ideal for Date & Batch Coding"
    ],
    color: "from-blue-500 to-cyan-500"
  },
  {
    icon: Droplet,
    title: "CIJ Printers",
    description: "Continuous Inkjet printers built for high-speed, non-contact printing in demanding industrial environments.",
    features: [
      "High-Speed Printing",
      "Suitable for Porous & Non-Porous Surfaces",
      "Long Run Times",
      "Low Ink Consumption"
    ],
    color: "from-indigo-500 to-purple-500"
  },
  {
    icon: Layers,
    title: "TTO Printers",
    description: "Thermal Transfer Overprint printers offering crisp, permanent prints on flexible packaging films and labels.",
    features: [
      "High Print Quality",
      "Ribbon-Based Printing",
      "Perfect for Food & Pharma Packaging",
      "Easy Integration with Production Lines"
    ],
    color: "from-orange-500 to-red-500"
  },
  {
    icon: Scale,
    title: "Industrial Weighing Machines",
    description: "Accurate, durable, and smart weighing systems tailored for retail, production, and logistics applications.",
    features: [
      "Precision Measurement",
      "Heavy-Duty Design",
      "Data Connectivity & Integration",
      "Customizable Display Options"
    ],
    color: "from-green-500 to-emerald-500"
  }
];

  const retailServices = [
    {
      icon: Smartphone,
      title: "Electronic Shelf Labels",
      description: "Smarter pricing and promotions with real-time updates.",
      features: ["Centralized price management","Wireless & paperless labels","Improved store efficiency","Eco-friendly solution"],
      color: "from-blue-500 to-purple-500"
    },
    {
      icon: Palette,
      title: "EAS Systems",
      description: "Protect your merchandise with advanced anti-theft technology.",
      features: ["Anti-theft antennas","Mobile & device security stands","Theft prevention systems","Reliable store protection"],
      color: "from-pink-500 to-violet-500"
    },
    {
      icon: TrendingUp,
      title: "Retail Weighing Scales",
      description: "Ensure accuracy and speed in every transaction.",
      features: ["High-precision weighing","Retail-friendly billing options","User-friendly interface","Faster checkout experience"],
      color: "from-green-500 to-blue-500"
    },
    {
      icon: Users,
      title: "POS Devices",
      description: "Modernize your checkout with powerful POS systems.",
      features: ["Easy billing and payments","Multiple integration options","Touchscreen and portable devices","Boost operational efficiency"],
      color: "from-orange-500 to-red-500"
    },
    {
      icon: Target,
      title: "Mobile Security",
      description: "Protect your smartphones and gadgets on display.",
      features: ["Secure stands & locks","Anti-theft alarms","Safe yet interactive shopping","Flexible display options"],
      color: "from-cyan-500 to-blue-500"
    },
    {
      icon: Globe,
      title: "Barcode Systems ",
      description: "Speed up operations with reliable barcode technology.",
      features: ["Fast, accurate scanning","Rugged & lightweight models","High-quality barcode/label printing","Improve inventory management"],
      color: "from-indigo-500 to-purple-500"
    }
  ];

  const currentServices = activeTab === 'industrial' ? industrialServices : retailServices;

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 min-h-screen font-poppins">
      <div className="max-w-7xl mx-auto px-6">
        
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-6">
            Our Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            We deliver comprehensive solutions tailored to your business needs, whether you're in industrial operations or retail commerce.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-16">
          <div className="bg-white p-2 rounded-full shadow-xl border border-gray-100">
            <div className="flex space-x-2">
              <button
                onClick={() => setActiveTab('industrial')}
                className={`flex items-center space-x-3 px-8 py-4 rounded-full transition-all duration-500 font-semibold text-lg ${
                  activeTab === 'industrial'
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg scale-105'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <Building2 size={24} />
                <span>Industrial Solutions</span>
              </button>
              <button
                onClick={() => setActiveTab('retail')}
                className={`flex items-center space-x-3 px-8 py-4 rounded-full transition-all duration-500 font-semibold text-lg ${
                  activeTab === 'retail'
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg scale-105'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <ShoppingBag size={24} />
                <span>Retail Solutions</span>
              </button>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {currentServices.map((service, index) => (
            <div
              key={`${activeTab}-${index}`}
              className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100 relative overflow-hidden"
              style={{
                animationDelay: `${index * 100}ms`,
                animation: `fadeInUp 0.6s ease-out forwards`
              }}
            >
              {/* Background Gradient */}
              <div className={`absolute inset-0 bg-gradient-to-r ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
              
              {/* Service Icon */}
              <div className={`w-16 h-16 bg-gradient-to-r ${service.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <service.icon size={28} className="text-white" />
              </div>

              {/* Service Content */}
              <h3 className="text-2xl font-bold text-gray-800 mb-4 group-hover:text-gray-900 transition-colors duration-300">
                {service.title}
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {service.description}
              </p>

              {/* Features List */}
              <ul className="space-y-3 mb-6">
                {service.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center space-x-3">
                    <CheckCircle size={16} className={`bg-gradient-to-r ${service.color} bg-clip-text text-transparent flex-shrink-0`} />
                    <span className="text-gray-600 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Learn More Button */}
              <button className={`group/btn w-full py-3 px-6 bg-gradient-to-r ${service.color} text-white rounded-xl font-semibold transition-all duration-300 hover:shadow-lg transform hover:scale-105 flex items-center justify-center space-x-2`}>
                <span>Learn More</span>
                <ArrowRight size={16} className="group-hover/btn:translate-x-1 transition-transform duration-300" />
              </button>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 text-center">
          <div className="bg-white rounded-3xl p-12 shadow-xl border border-gray-100 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-blue-500/5"></div>
            <div className="relative z-10">
              <h3 className="text-3xl font-bold text-gray-800 mb-4">
                Ready to Transform Your Business?
              </h3>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto text-lg">
                Let's discuss how our {activeTab === 'industrial' ? 'industrial' : 'retail'} solutions can drive your success forward.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="px-8 py-4 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2">
                  <span>Get Free Consultation</span>
                  <ArrowRight size={18} />
                </button>
                <button className="px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:border-purple-500 hover:text-purple-500 transition-all duration-300">
                  View Case Studies
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
}